import { submit, SubmissionError, getFormValues} from 'redux-form';
import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../../../../shared/form-elements/select';
import checkIsExcludedCountryCode from '../../../../shared/checkIsExcludedCountryCode';
import { actions as parentActions } from '../../../editor';

import groupBy from 'lodash/groupBy';
import forEach from 'lodash/forEach';
import flatten from 'lodash/flatten';
import isNaN from 'lodash/isNaN';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';

const isRocket = process.env.CPA_ENV === 'rocket';

const defaultInitialState = {
	field: {
		offer_id: null,
		location_id: '',
		is_own_callcenter: false,
		price: null,
		is_contest_enabled: false,
		is_active: true,
		affiliate_ids: null,
		contest_points: 0,
		display_priority: 0,
		status: 'active',
		currency: null,
		amount: null,
		form_boost_value: 0,
		boost_value: 0,
		cap: null,
		...(isRocket ? { competence_percent: 20} : { max_usd_payout: null }),
		is_booster_available: !isRocket
	},
	activeCheckboxStates: [],
	isCommentFormVisible: false
};

const createTargetingForm = ({ namespace, stateNodeName }) => {
	// action types
	const FETCH_SUGGEST_SUCCESS = `${namespace}/fetch-suggest-success`;
	const FETCH_SUGGEST_ERROR = `${namespace}/fetch-suggest-error`;

	const FETCH_AFFILIATES_SUGGEST_SUCCESS = `${namespace}/fetch-affiliates-suggest-success`;

	const TARGETING_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const TARGETING_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const MAX_PAYOUT_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const MAX_PAYOUT_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const TOGGLE_COMMENT_FORM = `${namespace}/${stateNodeName}-toggle-comment-form`;

	const COMMENT_FORM_STORE_IMAGE = `${namespace}/${stateNodeName}-comment-form-store-image`;

	const TARGETING_DELETE_SUCCESS = `${namespace}/${stateNodeName}-delete-success`;
	const TARGETING_DELETE_ERROR = `${namespace}/${stateNodeName}-delete-error`;

	const ROTATE_ACTIVE_CHECKBOX = `${namespace}/${stateNodeName}-rotate-checkbox`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		targetingFromSubmit: (formData, reason = {}) => (dispatch, getState) => {
			const { targetingForm, offer } = getState().programsEditor;
			const currencies = getState().root.lists['available-currencies'];
			const { targeting } = formData;
			const activeCheckboxStates = selectors.getActiveCheckboxStates(getState());

			const fields = targeting.map((field, index) => {
				const displayPriority = targeting.length - 1 - index;
				const activeState = find(activeCheckboxStates, {
					locationId: field.location_id && field.location_id[0] && field.location_id[0].value
				});

				field.boost_value = Math.round(field.form_boost_value * 100) / 10000;

				const correctFields = currencies.map(currency => ({
					...targetingForm.field,
					...field,
					display_priority: displayPriority,
					offer_id: offer.id,
					location_id: field['location_id'] && field['location_id'][0] ? field['location_id'][0]['value'] : '',
					currency: currency.id,
					amount: field[currency.id],
					status: 'active',
					...reason,
					contest_points: field.contest_points || null,
					is_active: activeState && activeState.value === 'true' ? true : false,
					affiliate_ids:
						activeState && activeState.value === 'indeterminate'
							? field['affiliate_ids'] && field['affiliate_ids'].map(obj => obj.value)
							: null
				}));

				return correctFields.length
					? correctFields
					: {
						...targetingForm.field
					};
			});

			const request = http({
				url: endpoints.OFFERS.TARGETING.replace('{id}', offer.id),
				method: 'put',
				actions: [TARGETING_FORM_SUBMIT_SUCCESS, TARGETING_FORM_SUBMIT_ERROR],
				data: flatten(fields).filter(t => t.currency !== 'rub')
			});

			const maxPayoutRequest = http({
				url: endpoints.OFFERS.MAX_PAYOUT.replace('{id}', offer.id),
				method: 'put',
				actions: [MAX_PAYOUT_SUBMIT_SUCCESS, MAX_PAYOUT_SUBMIT_ERROR],
				data: flatten(fields).filter(t => t.currency === 'usd')
			});

			return request(dispatch).then(async action => {
				if (!isRocket) {
					await maxPayoutRequest(dispatch);
				}

				if (action.type === TARGETING_FORM_SUBMIT_SUCCESS) {
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchTargetingData(offer.id)).then(action => {
						dispatch(parentActions.fetchIndividualTerms(offer.id));
					});
				}

				if (action.type === TARGETING_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = [];

					formData['targeting'].forEach((field, index) => {
						validationsErrors[index] = {};

						if (!field.location_id || (Array.isArray(field.location_id) && !field.location_id.length)) {
							validationsErrors[index]['location_id'] = 'Location should not be empty';
						}

						if (!field.form_boost_value || field.form_boost_value < 1) {
							validationsErrors[index]['form_boost_value'] = 'Boost value can not be less than 1 or empty';
						}

						if (!field.price || isNaN(+field.price)) {
							validationsErrors[index]['price'] = 'Price should be > 0.01 and integer';
						}

						currencies.forEach(currency => {
							if (!field[currency.id] || isNaN(+field[currency.id])) {
								validationsErrors[index][currency.id] = 'Amount should be > 0.01 and integer';
							}
						});
					});

					throw new SubmissionError({
						targeting: validationsErrors
					});
				}
			});
		},

		targetingDelete: location => (dispatch, getState) => {
			if (!location.length) {
				// Submit form for SubmissionError
				dispatch(submit('programsTargetingForm'));
				return;
			}
			const { offer } = getState().programsEditor;
			const url = endpoints.OFFERS.TARGETING.replace('{id}', offer.id) + '/' + location[0]['value'];
			const request = http({
				url,
				method: 'delete',
				actions: [TARGETING_DELETE_SUCCESS, TARGETING_DELETE_ERROR]
			});

			dispatch(parentActions.toggleProgressBar());

			request(dispatch).then(action => {
				if (action.type === TARGETING_DELETE_SUCCESS) {
					dispatch(parentActions.fetchTargetingData(offer.id)).then(() =>
						dispatch(parentActions.fetchIndividualTerms(offer.id))
					);
				}
			});
		},

		locationsLookups: text => (dispatch, getState) => {
			const { targeting } = getFormValues('programsTargetingForm')(getState());
			const selectedCountryIds = [];

			targeting.forEach(entity => {
				if (entity['location_id'] && entity['location_id'].length) {
					selectedCountryIds.push(entity['location_id'][0]['value']);
				}
			});

			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					const { data } = action.payload;
					const filteredData = data
						.filter(entity => selectedCountryIds.indexOf(entity.id) === -1)
						.filter(entity => !checkIsExcludedCountryCode(entity.country_code));
					return {
						options: createSelectOptionsFromSysEnums(filteredData)
					};
				}
			});
		},

		affiliatesLookups: text => (dispatch, getState) => {
			const { offer } = getState().programsEditor;

			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}&offer_id=${offer.id}`,
				method: 'get',
				actions: [FETCH_AFFILIATES_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_AFFILIATES_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},

		rotateActiveCheckbox: field => {
			const location_id = field.location_id && field.location_id[0] && field.location_id[0].value;
			return {
				type: ROTATE_ACTIVE_CHECKBOX,
				payload: location_id
			};
		}
	};

	// reducers
	const reducers = {
		[ROTATE_ACTIVE_CHECKBOX]: (state, action) => {
			const locationId = action.payload;
			const activeCheckboxStates = state.targetingForm.activeCheckboxStates;
			const activeCheckboxItem = find(activeCheckboxStates, { locationId });

			return {
				...state,
				targetingForm: {
					...state.targetingForm,
					activeCheckboxStates: activeCheckboxItem ? activeCheckboxStates.map((item, index) => {
						if (item.locationId === locationId) {
							switch (item.value) {
								case 'true':
									return { ...item, value: 'indeterminate' };
								case 'indeterminate':
									return { ...item, value: 'false' };
								case 'false':
									return { ...item, value: 'true' };
								default:
									return { ...item, value: 'true' };
							}
						}
						return item;
					}) : activeCheckboxStates.concat({ locationId, value: 'true' })
				}
			};
		},
		[FETCH_SUGGEST_ERROR]: errorReducerFn,
		[TOGGLE_COMMENT_FORM]: (state, action) => ({
			...state,
			targetingForm: {...state.targetingForm, isCommentFormVisible: !state.targetingForm.isCommentFormVisible}
		}),
		[COMMENT_FORM_STORE_IMAGE]: (state, action) => ({
			...state,
			targetingForm: {...state.targetingForm, reason_image: action.payload}
		}),
		[TARGETING_FORM_SUBMIT_ERROR]: errorReducerFn,
		[TARGETING_DELETE_ERROR]: errorReducerFn
	};

	// selectors
	const selectors = {
		getTargetingFormData: state => {
			const { targeting } = state.programsEditor;
			const fields = getTargetValues(targeting);

			return {
				initialValues: {
					targeting: orderBy(fields, ['display_priority'], ['desc'])
				}
			};
		},
		getActiveCheckboxStates: state => state.programsEditor.targetingForm.activeCheckboxStates,
		getActivePayoutCurrencies: state => state.root.lists['payout-currencies'].map(i => i.id)
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return { ...state, error: true, showProgressBar: false };
	}
	return state;
};

export const getTargetValues = targeting => {
	const fields = [];

	forEach(groupBy(targeting, 'location_id'), groupedFields => {
		let mergedField = {};
		forEach(groupedFields, field => {
			mergedField = {
				...mergedField,
				...field,
				location_id: [
					{
						label: field.location,
						value: field.location_id
					}
				],
				is_individual_term: field.is_individual_term || mergedField.is_individual_term, //true if any is true
				[field.currency]: field.amount,
				affiliate_ids: field.affiliate_ids ? field.affiliate_ids.map(obj => ({ value: obj.id, label: obj.text })) : null
			};
		});
		fields.push(mergedField);
	});

	return fields;
};

export default createTargetingForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/components/forms/targeting/index.js